import React, { ReactNode } from 'react';
import { Box, TextField } from '@p81-common/ui';
import { MaskedTextField } from '../ui';
import {
  CloudInfraCredentials,
  CloudInfraField,
  isAdminEmailValid,
  isClientIdValid,
  isSecretKeyValid
} from './helpers';
import { createClasses } from './styles';

export interface CredentialsFieldLabels {
  apiKeyInfoBody: ReactNode;
  clientIdLabel: string;
  clientIdPlaceholder: string;
  secretKeyLabel: string;
  secretKeyPlaceholder: string;
  showSecretKey: string;
  hideSecretKey: string;
  adminEmailInfoBody: ReactNode;
  adminEmailLabel: string;
  adminEmailPlaceholder: string;
  errors: {
    required: string;
    invalidEmail: string;
  };
}

interface CredentialsFieldsProps {
  values: CloudInfraCredentials;
  onChange: (patch: Partial<CloudInfraCredentials>) => void;
  touched: Record<CloudInfraField, boolean>;
  onBlur: (field: CloudInfraField) => void;
  labels: CredentialsFieldLabels;
}

const CredentialsFields = ({
  values,
  onChange,
  touched,
  onBlur,
  labels
}: CredentialsFieldsProps) => {
  const classes = createClasses();

  const clientIdError = touched.clientId && !isClientIdValid(values.clientId);
  const secretKeyError =
    touched.secretKey && !isSecretKeyValid(values.secretKey);
  const adminEmailError =
    touched.adminEmail && !isAdminEmailValid(values.adminEmail);

  return (
    <>
      <Box className={classes.credentialsGrid}>
        <TextField
          value={values.clientId}
          label={labels.clientIdLabel}
          labelWithHintProps={{ tooltipText: labels.apiKeyInfoBody }}
          placeholder={labels.clientIdPlaceholder}
          onChange={e => onChange({ clientId: e.target.value })}
          onBlur={() => onBlur('clientId')}
          error={clientIdError}
          helperText={clientIdError ? labels.errors.required : ''}
          fullWidth
        />
        <MaskedTextField
          value={values.secretKey}
          onChange={secretKey => onChange({ secretKey })}
          label={labels.secretKeyLabel}
          placeholder={labels.secretKeyPlaceholder}
          onBlur={() => onBlur('secretKey')}
          error={secretKeyError}
          helperText={secretKeyError ? labels.errors.required : ''}
          showAriaLabel={labels.showSecretKey}
          hideAriaLabel={labels.hideSecretKey}
        />
      </Box>

      <Box className={classes.section}>
        <TextField
          type="email"
          value={values.adminEmail}
          label={labels.adminEmailLabel}
          labelWithHintProps={{ tooltipText: labels.adminEmailInfoBody }}
          placeholder={labels.adminEmailPlaceholder}
          onChange={e => onChange({ adminEmail: e.target.value })}
          onBlur={() => onBlur('adminEmail')}
          error={adminEmailError}
          helperText={adminEmailError ? labels.errors.invalidEmail : ''}
          fullWidth
        />
      </Box>
    </>
  );
};

export default CredentialsFields;
